// src/pages/Motors/SellingVehiclesPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  Gauge,
  Fuel,
  CalendarDays,
  IndianRupee,
  X,
  Phone,
  MapPin,
  CarFront,
  ChevronLeft,
  ChevronRight,
  Inbox,
  RefreshCw,
} from "lucide-react";
import { vehicleSellService } from "../../services/vehicleSellService";

const inr = (n) => Number(n || 0).toLocaleString("en-IN");

/* ============================================================
   DETAIL MODAL
   ============================================================ */
const VehicleDetailModal = ({ vehicle, onClose }) => {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    setActiveImage(0);
  }, [vehicle]);

  if (!vehicle) return null;

  const images = vehicle.images || [];

  const nextImage = () =>
    setActiveImage((i) => (i + 1) % Math.max(images.length, 1));
  const prevImage = () =>
    setActiveImage(
      (i) => (i - 1 + Math.max(images.length, 1)) % Math.max(images.length, 1)
    );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[92vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-start justify-between border-b border-[#E2E8E5] px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#00A260]">
              {vehicle.vehicle_type || "Vehicle"}
            </p>
            <h3 className="mt-1 text-lg font-extrabold text-[#061A3A]">
              {vehicle.vehicle_brand} {vehicle.vehicle_model}
              {vehicle.vehicle_variant ? ` ${vehicle.vehicle_variant}` : ""}
            </h3>
            <p className="mt-0.5 text-xs text-[#94A3B8]">
              {[vehicle.city, vehicle.state].filter(Boolean).join(", ") || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[#E2E8E5] p-2 text-[#64748B] hover:bg-[#F1F5F3]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto">
          {/* IMAGES */}
          {images.length > 0 && (
            <div className="relative aspect-[16/10] w-full bg-[#F1F5F3]">
              <img
                src={images[activeImage]}
                alt="Vehicle"
                className="h-full w-full object-cover"
              />

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#061A3A] shadow-md hover:bg-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#061A3A] shadow-md hover:bg-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-[10px] font-semibold text-white">
                    {activeImage + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
          )}

          {/* THUMBNAILS */}
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto border-b border-[#E2E8E5] bg-[#FAFCFB] p-3">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImage(i)}
                  className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                    i === activeImage
                      ? "border-[#00A260]"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={`View ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* INFO */}
          <div className="space-y-5 px-5 py-5">
            {/* PRICE */}
            <div className="rounded-2xl bg-[#F3FBF7] p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#005543]">
                Expected Price
              </p>
              <p className="mt-1 text-2xl font-extrabold text-[#00A260]">
                ₹{inr(vehicle.expected_price)}
                {vehicle.negotiable ? (
                  <span className="ml-2 text-xs font-semibold text-[#005543]">
                    Negotiable
                  </span>
                ) : null}
              </p>
            </div>

            {/* SPECS */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Spec icon={CalendarDays} label="Year" value={vehicle.manufacturing_year} />
              <Spec icon={Fuel} label="Fuel" value={vehicle.fuel_type} />
              <Spec icon={Gauge} label="KM Driven" value={vehicle.km_driven ? `${inr(vehicle.km_driven)} km` : null} />
              <Spec
                icon={CarFront}
                label="Transmission"
                value={vehicle.transmission}
              />
              <Spec
                icon={CarFront}
                label="Owners"
                value={
                  vehicle.owners_count
                    ? `${vehicle.owners_count}${
                        vehicle.owners_count === 1
                          ? "st"
                          : vehicle.owners_count === 2
                          ? "nd"
                          : vehicle.owners_count === 3
                          ? "rd"
                          : "th"
                      } owner`
                    : null
                }
              />
              <Spec icon={CarFront} label="Color" value={vehicle.color} />
              <Spec
                icon={CarFront}
                label="Registration"
                value={vehicle.registration_number}
              />
              <Spec
                icon={CarFront}
                label="Reg. State"
                value={vehicle.registration_state}
              />
              <Spec
                icon={CalendarDays}
                label="Insurance"
                value={vehicle.insurance_valid_till}
              />
            </div>

            {/* DESCRIPTION */}
            {vehicle.description ? (
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                  Description
                </p>
                <p className="whitespace-pre-line text-sm leading-6 text-[#061A3A]">
                  {vehicle.description}
                </p>
              </div>
            ) : null}

            {/* CONTACT */}
            <div className="rounded-2xl border border-[#E2E8E5] p-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                Contact Seller
              </p>
              <p className="text-sm font-semibold text-[#061A3A]">
                {vehicle.owner_name}
              </p>
              {vehicle.city ? (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-[#64748B]">
                  <MapPin className="h-3.5 w-3.5 text-[#00A260]" />
                  {vehicle.city}
                  {vehicle.state ? `, ${vehicle.state}` : ""}
                </p>
              ) : null}

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <a
                  href={`tel:${vehicle.phone}`}
                  className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#00A260] text-sm font-bold text-white transition hover:bg-[#005543]"
                >
                  <Phone className="h-4 w-4" />
                  Call Seller
                </a>
                <a
                  href={`https://wa.me/${
                    (vehicle.whatsapp || vehicle.phone || "").replace(/\D/g, "")
                  }`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-[#00A260] bg-white text-sm font-bold text-[#00A260] transition hover:bg-[#F3FBF7]"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Spec = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="rounded-xl border border-[#E2E8E5] bg-white p-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[#00A260]" />
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
          {label}
        </p>
      </div>
      <p className="mt-1 text-sm font-semibold text-[#061A3A]">{value}</p>
    </div>
  );
};

/* ============================================================
   GRID CARD
   ============================================================ */
const VehicleCard = ({ vehicle, onClick }) => {
  const cover = (vehicle.images || [])[0];
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#E2E8E5] bg-white text-left transition hover:-translate-y-0.5 hover:border-[#00A260]/40 hover:shadow-lg"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#F1F5F3]">
        {cover ? (
          <img
            src={cover}
            alt={`${vehicle.vehicle_brand} ${vehicle.vehicle_model}`}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-[#94A3B8]">
            <CarFront className="h-8 w-8" />
          </div>
        )}

        <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-bold uppercase text-[#061A3A] backdrop-blur">
          {vehicle.vehicle_type || "Vehicle"}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="truncate text-sm font-bold text-[#061A3A]">
          {vehicle.vehicle_brand} {vehicle.vehicle_model}
        </h3>
        <p className="mt-0.5 text-[11px] text-[#64748B]">
          {[
            vehicle.vehicle_variant,
            vehicle.manufacturing_year,
            vehicle.fuel_type,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-base font-extrabold text-[#00A260]">
            ₹{inr(vehicle.expected_price)}
          </p>
          {vehicle.km_driven ? (
            <p className="text-[11px] text-[#64748B]">
              {inr(vehicle.km_driven)} km
            </p>
          ) : null}
        </div>

        {vehicle.city ? (
          <p className="mt-2 flex items-center gap-1 text-[10px] text-[#94A3B8]">
            <MapPin className="h-3 w-3" />
            {vehicle.city}
          </p>
        ) : null}
      </div>
    </button>
  );
};

/* ============================================================
   MAIN PAGE
   ============================================================ */
const SellingVehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const data = await vehicleSellService.listPublishedVehicles({
        search: search || undefined,
        brand: brandFilter || undefined,
      });
      setVehicles(data);
    } catch (err) {
      console.error("Load vehicles error:", err);
      setError(err?.message || "Unable to load vehicles");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const brands = useMemo(() => {
    const set = new Set(
      vehicles.map((v) => v.vehicle_brand).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [vehicles]);

  return (
    <main className="min-h-screen bg-[#F8FAF9] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* HEADER */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#061A3A] text-[#8BF108]">
                <CarFront className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold text-[#061A3A] sm:text-3xl">
                  Vehicles for Sale
                </h1>
                <p className="text-xs text-[#64748B]">
                  Browse verified vehicles listed by owners
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-[#E2E8E5] bg-white px-4 text-xs font-semibold text-[#061A3A] transition hover:border-[#00A260] hover:text-[#00A260]"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>

            <a
              href="/motors/sell"
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#00A260] px-4 text-xs font-bold text-white transition hover:bg-[#005543]"
            >
              Sell Your Vehicle
            </a>
          </div>
        </div>

        {/* FILTERS */}
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-[#E2E8E5] bg-white p-3 sm:flex-row sm:items-center">
          <form onSubmit={handleSearch} className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search brand, model, city…"
                className="h-11 w-full rounded-xl border border-[#E2E8E5] bg-[#FAFCFB] pl-10 pr-4 text-sm text-[#061A3A] outline-none focus:border-[#00A260] focus:ring-4 focus:ring-[#00A260]/10"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-[#00A260] px-4 text-xs font-bold text-white hover:bg-[#005543]"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-[#94A3B8]" />
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="h-11 rounded-xl border border-[#E2E8E5] bg-white px-3 text-xs font-semibold text-[#061A3A] outline-none focus:border-[#00A260] focus:ring-4 focus:ring-[#00A260]/10"
            >
              <option value="">All Brands</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* GRID */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse overflow-hidden rounded-2xl border border-[#E2E8E5] bg-white"
              >
                <div className="aspect-[16/10] bg-[#F1F5F3]" />
                <div className="space-y-2 p-4">
                  <div className="h-3 w-3/4 rounded bg-[#F1F5F3]" />
                  <div className="h-3 w-1/2 rounded bg-[#F1F5F3]" />
                  <div className="h-4 w-1/3 rounded bg-[#F1F5F3]" />
                </div>
              </div>
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E2E8E5] bg-white p-12 text-center">
            <Inbox className="mx-auto h-10 w-10 text-[#CBD5D1]" />
            <p className="mt-3 text-sm font-semibold text-[#64748B]">
              No vehicles listed yet
            </p>
            <p className="mt-1 text-xs text-[#94A3B8]">
              Be the first to list your vehicle.
            </p>
            <a
              href="/motors/sell"
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl bg-[#00A260] px-4 text-xs font-bold text-white hover:bg-[#005543]"
            >
              Sell Your Vehicle
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {vehicles.map((v) => (
              <VehicleCard key={v.id} vehicle={v} onClick={() => setSelected(v)} />
            ))}
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      <VehicleDetailModal vehicle={selected} onClose={() => setSelected(null)} />
    </main>
  );
};

export default SellingVehiclesPage;